import { Canvas, Textbox } from 'fabric';
import { useMemo } from 'react';

import { Tool } from '../canvas/draw-canvas.constants';
import { useDrawCanvasContext } from '../canvas/draw-canvas.context';
import ToolbarItemText from '../canvas/toolbar/toolbar-item-text';
import ToolbarItemSelect from '../canvas/toolbar/toolbar-item-select';
import ToolbarItemPencilColour from '../canvas/toolbar/toolbar-item-pencil-colour';
import ToolbarItemSize from '../canvas/toolbar/toolbar-item-size';
import ToolTextStyle from '../canvas/toolbar/tool-text-style';
import ToolbarItemShapeStyles from '../canvas/toolbar/toolbar-item-shape-styles';
import ToolbarItem from '../canvas/toolbar/toolbar-item';
import * as Styled from '../canvas/toolbar/toolbar.style';
import { isTextbox } from '../canvas/logic/is-textbox';
import { isCircle } from '../canvas/logic/is-circle';
import { isTriangle } from '../canvas/logic/is-triangle';

import IconRedo from 'approot/shared/inline-svg/activity-builder/icon-redo';
import IconUndo from 'approot/shared/inline-svg/activity-builder/icon-undo';
import IconZoomOut from 'approot/shared/inline-svg/activity-builder/icon-zoom-out';
import IconZoomIn from 'approot/shared/inline-svg/activity-builder/icon-zoom-in';
import ToolbarItemCursor from 'approot/activity-builder/components/draw/canvas/toolbar/toolbar-item-cursor';

type Props = {
  canvas?: Canvas | null;
  canUndo: boolean;
  canRedo: boolean;
  increaseZoom: () => void;
  decreaseZoom: () => void;
  onUndo?: () => void;
  onRedo?: () => void;
};

const CustomBackgroundToolbar = ({
  canvas,
  canUndo,
  canRedo,
  increaseZoom,
  decreaseZoom,
  onUndo,
  onRedo,
}: Props) => {
  const {
    toolSet,
    eraserBrush,
    setEraserDrawing,
    textTool,
    setTextTool,
    setStrokeColour,
    setFillColour,
  } = useDrawCanvasContext();

  const toolSizeSettings = useMemo(() => {
    return {
      tool: eraserBrush,
      scale: {
        min: 1,
        max: 50,
        step: 1,
      },
    };
  }, [eraserBrush]);

  const activeObject = canvas?.getActiveObject();

  return (
    <Styled.ToolbarContainer>
      <Styled.Toolbar>
        <Styled.ToolbarCore>
          {activeObject &&
          (isCircle(activeObject) || isTriangle(activeObject)) ? (
            <Styled.ToolbarContextualMenu>
              <Styled.ToolbarItems>
                <ToolbarItemShapeStyles
                  fillColour={activeObject.get('fill')}
                  strokeColour={activeObject.get('stroke')}
                  onSelectFill={colour => {
                    activeObject.set('fill', colour);
                    activeObject.canvas?.fire('object:modified');
                    canvas?.renderAll();

                    setFillColour(colour);
                  }}
                  onSelectStroke={colour => {
                    activeObject.set('stroke', colour);
                    activeObject.canvas?.fire('object:modified');
                    canvas?.renderAll();

                    setStrokeColour(colour);
                  }}
                />
              </Styled.ToolbarItems>
            </Styled.ToolbarContextualMenu>
          ) : toolSet?.tool === Tool.Eraser ? (
            <Styled.ToolbarContextualMenu>
              <Styled.ToolbarItems>
                <ToolbarItemSize
                  tool={toolSizeSettings.tool}
                  scale={toolSizeSettings.scale}
                  hoverText="Eraser Style"
                  setSize={size => {
                    if (canvas) {
                      setEraserDrawing(canvas, {
                        ...eraserBrush,
                        size,
                      });
                    }
                  }}
                />
              </Styled.ToolbarItems>
            </Styled.ToolbarContextualMenu>
          ) : isTextbox(activeObject) ? (
            <Styled.ToolbarContextualMenu>
              <Styled.ToolbarItems>
                <ToolbarItemPencilColour
                  toolColour={activeObject.get('fill') as string}
                  hoverText="Text Colour"
                  onSelect={colour => {
                    activeObject.set('fill', colour);
                    activeObject.canvas?.fire('object:modified');
                    canvas?.renderAll();

                    setTextTool({
                      ...textTool,
                      colour,
                    });
                  }}
                />
                <ToolTextStyle
                  size={activeObject.get('fontSize')}
                  font={activeObject.get('fontFamily')}
                  scale={toolSizeSettings.scale}
                  onSelect={size => {
                    activeObject.set('fontSize', size);
                    activeObject.canvas?.fire('object:modified');
                    canvas?.renderAll();

                    setTextTool({
                      ...textTool,
                      size,
                    });
                  }}
                  onFontSelect={font => {
                    activeObject.set('fontFamily', font);
                    activeObject.setCoords();
                    (activeObject as Textbox).initDimensions();
                    activeObject.canvas?.fire('object:modified');
                    canvas?.renderAll();
                  }}
                />
              </Styled.ToolbarItems>
            </Styled.ToolbarContextualMenu>
          ) : null}

          <Styled.ToolbarItems>
            <ToolbarItemText canvas={canvas} />
            <Styled.ToolbarMenuDivider />
            <ToolbarItemSelect canvas={canvas} />
            <ToolbarItemCursor canvas={canvas} />

            <Styled.ToolbarMenuDivider />
            <ToolbarItem hoverText="Undo">
              <Styled.ToolBarTool disabled={!canUndo} onClick={onUndo}>
                <IconUndo width={24} height={24} />
              </Styled.ToolBarTool>
            </ToolbarItem>
            <ToolbarItem hoverText="Redo">
              <Styled.ToolBarTool disabled={!canRedo} onClick={onRedo}>
                <IconRedo width={24} height={24} />
              </Styled.ToolBarTool>
            </ToolbarItem>
          </Styled.ToolbarItems>

          <Styled.ToolbarItems>
            <ToolbarItem hoverText="Zoom out">
              <Styled.ToolBarTool onClick={decreaseZoom}>
                <IconZoomOut width={24} height={24} />
              </Styled.ToolBarTool>
            </ToolbarItem>
            <ToolbarItem hoverText="Zoom in">
              <Styled.ToolBarTool onClick={increaseZoom}>
                <IconZoomIn width={24} height={24} />
              </Styled.ToolBarTool>
            </ToolbarItem>
          </Styled.ToolbarItems>
        </Styled.ToolbarCore>
      </Styled.Toolbar>
    </Styled.ToolbarContainer>
  );
};

export default CustomBackgroundToolbar;
